import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  // Keep section-renderer out of the initial dependency graph until hydration is actually needed.
  const { buildSectionSelector, normalizeSectionId, sectionRenderer } = await import('@theme/section-renderer');
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });

  section.dataset.hydrated = 'true';
}

/**
 * Returns true only for the Rachelle product-page header hydration.
 * Other sections and templates keep Horizon's regular idle hydration.
 *
 * @param {string} sectionId
 * @returns {boolean}
 */
function shouldDeferRachelleHeaderHydration(sectionId) {
  if (!document.body.classList.contains('rachelle-product-page-template')) return false;

  const section = document.getElementById(`shopify-section-${sectionId}`);
  return Boolean(section?.querySelector('#header-component'));
}

/**
 * Defers the optional rich header hydration until the visitor actually interacts
 * with the navigation. The initial drawer remains fully usable with its server-rendered links.
 *
 * @param {string} sectionId
 * @param {URL} [url]
 */
function hydrateRachelleHeaderOnDemand(sectionId, url) {
  const header = document.querySelector('#header-component');
  if (!header) return;

  let hydrationRequested = false;

  const requestHydration = (event) => {
    if (hydrationRequested) return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('#Details-menu-drawer-container, header-menu')) return;

    hydrationRequested = true;
    header.removeEventListener('pointerover', requestHydration, true);
    header.removeEventListener('focusin', requestHydration, true);
    header.removeEventListener('click', requestHydration, true);

    requestIdleCallback(() => hydrateSection(sectionId, url));
  };

  header.addEventListener('pointerover', requestHydration, true);
  header.addEventListener('focusin', requestHydration, true);
  header.addEventListener('click', requestHydration, true);
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    if (shouldDeferRachelleHeaderHydration(sectionId)) {
      hydrateRachelleHeaderOnDemand(sectionId, url);
      return;
    }

    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}
